import "./App.css";
import { Amplify, I18n } from "aws-amplify";
import { Authenticator, translations } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { MacsRouter } from "./components/MacsRouter";
import { MacsRouterUnauth } from "./components/MacsRouterUnauth";
import { UserProvider } from "./context/UserContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CourseStructureProvider } from "./context/CourseStructureContext";
import { TempEventProvider } from "./context/TempEventContext";
import { UniversalModalProvider } from "./context/UniversalModalContext";
import { cognitoConfig } from "./CognitoConfig";
import { BrowserRouter } from "react-router-dom";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import { InsightsProgressFilterProvider } from "./context/InsightsProgressFilterContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { useMacsAuthenticator } from "./hooks/useMacsAuthenticator";
import { Backdrop, Box, CircularProgress } from "@mui/material";
import { InsightsProvider } from "./context/InsightsContext";
import { SessionExtensionDialog } from "./components/v2/SessionExtensionDialog";
import { LoggingOutDialog } from "./components/v2/LoggingOutDialog";
import { AWSWAFCaptchaModal } from "./components/utils/AWSWAFCaptchaModal";
import { _AuthType } from "./enums";
import { Helmet, HelmetProvider } from "react-helmet-async"; // Import HelmetProvider

I18n.putVocabularies(translations);
I18n.setLanguage("en");
I18n.putVocabulariesForLanguage("en", {
  "Sign in": "LOGIN", // Button label
  "Send code": "SEND CODE",
  "Back to Sign In": "Back to Sign In",
  Submit: "Continue",
  "Username/client id combination not found.": "Username/email does not exist.",
  "Temporary password has expired and must be reset by an administrator.":
    "Temporary password has expired. Please contact support if you need a new temporary password.",
});

Amplify.configure(cognitoConfig);

const MACSApp = () => {
  const authHook = useMacsAuthenticator();
  const {
    authenticated,
    initialized,
    authType,
    ltik,
    showSessionEndDialog,
    showExtensionDialog,
    exp,
    timeRemaining,
    isLoggingOut,
    handleExtensionPromptLogOut,
    handleExtensionPromptExtend,
  } = authHook;

  const isNonLtiOrLtiAndReady =
    (authType === _AuthType.LTI && ltik) || authType !== _AuthType.LTI;

  return (
    <div className="App" id="App">
      <TempEventProvider>
        <HelmetProvider>
          <Helmet>
            <title>Digit</title> {/*Default Title*/}
          </Helmet>
          <CourseStructureProvider authHook={authHook}>
            <UserProvider authHook={authHook}>
              <ThemeProvider>
                <UniversalModalProvider>
                  {!initialized || !isNonLtiOrLtiAndReady ? (
                    <Backdrop open>
                      <CircularProgress />
                    </Backdrop>
                  ) : authenticated ? (
                    <Box className="MainContent" sx={{ flexGrow: 1 }}>
                      <WebSocketProvider>
                        <InsightsProvider>
                          <InsightsProgressFilterProvider>
                            <MacsRouter />
                          </InsightsProgressFilterProvider>
                        </InsightsProvider>
                      </WebSocketProvider>
                      {process.env.REACT_APP_DEPLOYMENT_ENV === "develop" && authType && (
                        <div
                          style={{
                            position: "fixed",
                            left: "16px",
                            top: "16px",
                            color: "white",
                            backgroundColor: "red",
                            fontWeight: "bold",
                          }}>
                          {authType} Mode
                        </div>
                      )}
                      <>
                        <LoggingOutDialog open={showSessionEndDialog} />
                        {!showSessionEndDialog && (
                          <SessionExtensionDialog
                            timeRemaining={timeRemaining}
                            showExtensionDialog={showExtensionDialog}
                            exp={exp}
                            handleLogOut={handleExtensionPromptLogOut}
                            handleExtend={handleExtensionPromptExtend}
                            isLoggingOut={isLoggingOut}
                          />
                        )}
                      </>
                    </Box>
                  ) : (
                    <div>
                      <MacsRouterUnauth />
                    </div>
                  )}
                </UniversalModalProvider>
              </ThemeProvider>
            </UserProvider>
          </CourseStructureProvider>
        </HelmetProvider>
      </TempEventProvider>
    </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BrowserRouter>
        <Authenticator.Provider>
          <MACSApp />
          <ToastContainer />
          <AWSWAFCaptchaModal />
        </Authenticator.Provider>
      </BrowserRouter>
    </LocalizationProvider>
  </ErrorBoundary>
);

export default App;
